import { Link } from "@/i18n/Link";
import { Phone, ClipboardCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE, SITE_PHONE_DISPLAY, SITE_URL } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const BOOKING_URL = "https://axle.setmore.com/book";

const COPY = {
  en: {
    home: "Home",
    title: "Free 51-Point Trailer Inspection",
    seoDescription: "Our most comprehensive multi-point trailer evaluation - towing systems, running gear, enclosure hardware, and advanced braking components checked for safety and reliability. Schedule your free 51-point inspection today.",
    seoFullTitle: "Free 51-Point Trailer Inspection in Elkhart, IN | Axle Inc.",
    serviceDescription: "A comprehensive 51-point trailer evaluation covering towing systems, running gear, enclosure hardware, and advanced braking components to ensure every critical safety, structural, and braking component is operating reliably.",
    tagline: "Free Inspection",
    heroHeading: "Free 51-Point Trailer Inspection",
    heroSub1: "Our most comprehensive multi-point evaluation, designed to ensure every critical safety, structural, and braking component is operating reliably and performing at its best.",
    heroSub2: "This top-tier inspection includes an in-depth review of towing systems, running gear, enclosure hardware, and advanced braking components. By identifying wear, loose hardware, or potential failures early, we help minimize downtime, reduce repair costs, and keep your trailer road-ready for demanding use.",
    btnSchedule: "Schedule your inspection",
    btnScheduleFull: "Schedule your trailer inspection",
    highlightsTitle: "Inspection highlights",
    highlights: [
      "Trailer lights and wiring functionality",
      "Trailer jack, hitch, and coupler operation",
      "Safety chains and safety cables",
      "Door latches, hinges, and hinge cables",
      "Tires, wheels, axles, and spring hardware",
      "Hubs, studs, bearings, races, and seals",
      "Brake assemblies including pads, shoes, springs, and spring magnets",
      "Brake activator and full braking system response",
    ],
    resultsTitle: "The result",
    results: [
      "Highest level of safety and reliability",
      "Early detection of wear or potential failures",
      "Reduced risk of costly downtime",
      "Confidence on every mile and every job",
    ],
    footerPrompt: "Need work done afterward? Our shop handles ",
    linkAxleRepair: "trailer axle repair",
    and: " and ",
    linkEmergency: "emergency & same-day repair",
    linkCostGuide: "cost guide",
    footerText: " shows what typical repairs run.",
    brandPromise: "Keep your equipment protected and your trailer performing at its peak.",
    location: "53664 County Road 9, Elkhart, IN · Mon–Fri 8am–5pm"
  },
  es: {
    home: "Inicio",
    title: "Inspección de 51 puntos para remolques gratis",
    seoDescription: "Nuestra evaluación de remolques de múltiples puntos más completa: sistemas de remolque, tren de rodaje, accesorios de cerramiento y componentes de frenos avanzados revisados para mayor seguridad y confiabilidad. Programe su inspección de 51 puntos gratis hoy mismo.",
    seoFullTitle: "Inspección de 51 puntos para remolques gratis en Elkhart, IN | Axle Inc.",
    serviceDescription: "Una evaluación completa de 51 puntos para remolques que abarca sistemas de remolque, tren de rodaje, accesorios de cerramiento y componentes de frenos avanzados para garantizar que cada componente crítico de seguridad, estructural y de frenado funcione de manera confiable.",
    tagline: "Inspección gratis",
    heroHeading: "Inspección de 51 puntos para remolques gratis",
    heroSub1: "Nuestra evaluación de múltiples puntos más completa, diseñada para garantizar que cada componente crítico de seguridad, estructural y de frenado funcione de manera confiable y tenga el mejor rendimiento.",
    heroSub2: "Esta inspección de primer nivel incluye una revisión profunda de los sistemas de remolque, tren de rodaje, accesorios de cerramiento y componentes de frenos avanzados. Al identificar el desgaste, los herrajes sueltos o posibles fallas a tiempo, ayudamos a minimizar el tiempo de inactividad, reducir los costos de reparación y mantener su remolque listo para la carretera para un uso exigente.",
    btnSchedule: "Programe su inspección",
    btnScheduleFull: "Programe su inspección de remolque",
    highlightsTitle: "Aspectos destacados de la inspección",
    highlights: [
      "Funcionalidad de luces y cableado del remolque",
      "Operación del gato, enganche y acoplador del remolque",
      "Cadenas y cables de seguridad",
      "Pasadores de puerta, bisagras y cables de bisagra",
      "Neumáticos, ruedas, ejes y herrajes de la ballesta",
      "Mazas, pernos, rodamientos, pistas y sellos",
      "Conjuntos de frenos, incluyendo pastillas, zapatas, resortes e imanes de freno",
      "Activador de frenos y respuesta completa del sistema de frenado",
    ],
    resultsTitle: "El resultado",
    results: [
      "El más alto nivel de seguridad y confiabilidad",
      "Detección temprana de desgaste o posibles fallas",
      "Menor riesgo de costosos tiempos de inactividad",
      "Confianza en cada milla y en cada trabajo",
    ],
    footerPrompt: "¿Necesita reparaciones después? Nuestro taller realiza ",
    linkAxleRepair: "reparación de ejes de remolque",
    and: " y ",
    linkEmergency: "reparación de emergencia y el mismo día",
    linkCostGuide: "guía de costos",
    footerText: " muestra cuánto cuestan las reparaciones típicas.",
    brandPromise: "Mantenga su equipo protegido y su remolque funcionando al máximo rendimiento.",
    location: "53664 County Road 9, Elkhart, IN · Lun–Vie 8am–5pm"
  }
};

export default function Free51PointInspection() {
  const t = useCopy(COPY);
  const path = "/free-51-point-inspection/";
  const trail = [
    { name: t.home, path: "/" },
    { name: t.title, path },
  ];
  return (
    <PageLayout>
      <Seo
        title={t.seoFullTitle}
        description={t.seoDescription}
        path={path}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${SITE_URL}${path}#service`,
            name: t.title,
            serviceType: "Trailer inspection",
            provider: { "@id": `${SITE_URL}/#business` },
            description: t.serviceDescription,
            url: `${SITE_URL}${path}`,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            availableChannel: {
              "@type": "ServiceChannel",
              serviceUrl: BOOKING_URL,
              servicePhone: {
                "@type": "ContactPoint",
                telephone: SITE_PHONE,
                contactType: "customer service",
              },
            },
          },
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">
            <ClipboardCheck className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
            {t.tagline}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl">
            {t.heroHeading}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {t.heroSub1}
          </p>
          <p className="mt-4 text-secondary-foreground/80 max-w-3xl leading-relaxed">
            {t.heroSub2}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href={BOOKING_URL} target="_blank" rel="noopener">{t.btnSchedule}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.highlightsTitle}</h2>
        <ul className="grid gap-4 md:grid-cols-2 max-w-4xl">
          {t.highlights.map((h: string) => (
            <li key={h} className="flex items-start gap-3 bg-card border border-border rounded-md p-4">
              <ClipboardCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <span>{h}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.resultsTitle}</h2>
          <ul className="space-y-4 max-w-2xl">
            {t.results.map((r: string) => (
              <li key={r} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="font-medium">{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-10 text-muted-foreground max-w-3xl leading-relaxed">
            {t.footerPrompt}
            <Link to="/services/axle-repair/" className="text-primary-strong hover:underline underline-offset-4">{t.linkAxleRepair}</Link>
            {t.and}
            <Link to="/services/emergency-same-day-trailer-repair/" className="text-primary-strong hover:underline underline-offset-4">{t.linkEmergency}</Link>
            , and our{" "}
            <Link to="/trailer-axle-replacement-cost/" className="text-primary-strong hover:underline underline-offset-4">{t.linkCostGuide}</Link>
            {t.footerText}
          </p>
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">
              {t.brandPromise}
            </h2>
            <p className="mt-1 text-primary-foreground">
              {t.location}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button asChild size="lg" className="bg-white text-primary-strong hover:bg-white/90">
              <a href={BOOKING_URL} target="_blank" rel="noopener">{t.btnScheduleFull}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
